'use client';

import { AlternativeProductType, type components } from '~/fecom-api/schema';
import { FecomImage } from '../images/fecom-image';
import { InlineLink } from '../common/inline-link';
import { Price } from './price';
import StockStatus from './stock-status';
import { Button } from '../ui/button';
import Link from 'next/link';
import { cn } from '~/lib/utils';
import { useQuantity } from '~/hooks/product/use-quantity';
import { AddToBasketButton } from './add-to-basket-button';
import { gtmAddProductsToCart } from '~/utils/gtm/gtm-helpers';
import { ProductsILoveButton } from './products-i-love-button';
import { type BasketChange } from '~/context/basket-context';

type AlternativeProductProps = {
  product: AlternativeProduct;
  fecomMediaBaseUrl: string;
  className?: string;
} & (
  | {
      variant: 'pdp';
    }
  | {
      variant: 'basket';
      disabled?: boolean;
      isLoading?: boolean;
      onButtonClick?: () => void;
      change?: BasketChange;
      inStockDeliveryMessage?: string | null;
    }
);

export type AlternativeProduct = components['schemas']['Product'];

export function AlternativeProduct({
  fecomMediaBaseUrl,
  product,
  className,
  ...props
}: AlternativeProductProps) {
  const getTitle = () => {
    switch (product.alternativeProductType) {
      case AlternativeProductType.OutOfStockAlternative:
        return 'Need it now?';
      case AlternativeProductType.Classmates:
        return 'Switch & Save';
      case AlternativeProductType.UpSell:
        return 'You may also need';
      default:
        return '';
    }
  };

  const productUrl = product.urlPath;

  let showRemoveWarning = false;
  if (props.variant === 'basket' && props.change) {
    const currentQuantity =
      props?.variant === 'basket' && props.change?.quantity;
    showRemoveWarning =
      props?.variant === 'basket' &&
      props.change?.type === 'product' &&
      currentQuantity === 0;
  }

  const { minQuantity } = useQuantity({
    catalogueRef: product.catalogueRef,
    minimumOrderQuantity: product.minOrderQuantity,
  });

  const getButton = () => {
    if (
      props.variant === 'basket' &&
      product.alternativeProductType === AlternativeProductType.Classmates
    ) {
      return (
        <Button
          variant="info-outline"
          type="button"
          className="ml-auto text-info"
          onClick={props.onButtonClick}
          disabled={props.disabled}
          loader={props.isLoading}
        >
          Switch to Classmates
        </Button>
      );
    }
    if (
      props.variant === 'basket' &&
      product.alternativeProductType ===
        AlternativeProductType.OutOfStockAlternative
    ) {
      return (
        <Button
          variant="success-outline"
          type="button"
          className="ml-auto text-info"
          onClick={props.onButtonClick}
          disabled={props.disabled}
          loader={props.isLoading}
        >
          Switch to alternative
        </Button>
      );
    }

    if (product.alternativeProductType === AlternativeProductType.Classmates) {
      return (
        <AddToBasketButton
          className="ml-auto text-info"
          itemCode={product.itemCode}
          productName={product.productName}
          quantity={minQuantity}
          showInBasket
          buttonVariant="info-outline"
          onSuccess={() => {
            gtmAddProductsToCart({
              products: [[product, minQuantity]],
              currency: 'GBP',
              itemListId: 'pdp',
              itemListName: 'PDP',
            });
          }}
        />
      );
    }

    if (
      props.variant === 'basket' &&
      product.alternativeProductType === AlternativeProductType.UpSell
    ) {
      return (
        <AddToBasketButton
          className="ml-auto text-info"
          itemCode={product.itemCode}
          productName={product.productName}
          quantity={minQuantity}
          showInBasket
          buttonVariant="info-outline"
          onSuccess={() => {
            gtmAddProductsToCart({
              products: [[product, minQuantity]],
              currency: 'GBP',
              itemListId: 'pdp',
              itemListName: 'PDP',
            });
          }}
        />
      );
    }

    return (
      <Button
        variant="success-outline"
        className="text-success lg:ml-auto"
        size="sm"
        asChild
      >
        <Link href={productUrl}>Switch to alternative</Link>
      </Button>
    );
  };

  const renderMobile = () => {
    return (
      <div className="flex grow flex-col gap-2 md:hidden">
        <div className="flex flex-col gap-2">
          <div className="flex grow flex-col gap-2">
            <p className="text-xs">{product.brandName}</p>
            <h4 className="text-sm font-semibold leading-snug">
              <InlineLink
                href={productUrl}
                className="no-underline hover:underline"
                variant="inherit"
              >
                {product.productName}
              </InlineLink>
            </h4>
          </div>
        </div>
        <div className="flex items-start gap-4">
          <ProductsILoveButton
            catRef={product.catalogueRef}
            className="h-6 w-6"
            variant="main"
            isInitialFavourite={product.isFavourite}
          />
          <StockStatus
            classname="text-xs"
            stockInfo={product.stockInfo}
            isDirectDelivery={product.isDirectDelivery}
          />
          <div className="ml-auto shrink-0">
            <p className="leading-tight">
              <Price
                className="text-base font-bold"
                vatStatusClassName="text-xs"
                priceExcVat={product.priceExcVat}
                priceIncVat={product.priceIncVat}
                showVatStatusText
              />
            </p>
            <p className="leading-tight">
              <Price
                className="text-xs font-medium"
                vatStatusClassName="text-xs"
                priceExcVat={product.priceExcVat}
                priceIncVat={product.priceIncVat}
                showVatStatusText
                showOppositeVatStatus
              />
            </p>
          </div>
        </div>
        <div>{getButton()}</div>
      </div>
    );
  };

  const renderDesktop = () => {
    return (
      <div className="hidden grow flex-col gap-1 md:flex">
        <div className="flex gap-2">
          <div className="flex grow flex-col gap-2">
            <p className="text-sm">{product.brandName}</p>
            <h4 className="text-lg font-semibold leading-snug">
              <InlineLink
                href={productUrl}
                className="no-underline hover:underline"
                variant="inherit"
              >
                {product.productName}
              </InlineLink>
            </h4>
          </div>
          <div className="shrink-0">
            <p className="leading-tight">
              <Price
                className="text-lg font-bold"
                vatStatusClassName="text-xs"
                priceExcVat={product.priceExcVat}
                priceIncVat={product.priceIncVat}
                showVatStatusText
              />
            </p>
            <p className="leading-tight">
              <Price
                className="text-sm font-medium"
                vatStatusClassName="text-xs"
                priceExcVat={product.priceExcVat}
                priceIncVat={product.priceIncVat}
                showVatStatusText
                showOppositeVatStatus
              />
            </p>
          </div>
        </div>
        <div className="flex gap-4">
          <ProductsILoveButton
            catRef={product.catalogueRef}
            className="my-auto h-6 w-6"
            variant="main"
            isInitialFavourite={product.isFavourite}
          />
          <StockStatus
            stockInfo={product.stockInfo}
            isDirectDelivery={product.isDirectDelivery}
            classname="text-black"
            inStockDeliveryMessage={
              props.variant === 'basket' &&
              product.stockInfo?.stockLevel &&
              product.stockInfo.stockLevel > 5 &&
              props.inStockDeliveryMessage
                ? props.inStockDeliveryMessage
                : undefined
            }
          />
          {getButton()}
        </div>
      </div>
    );
  };

  return (
    <fieldset
      className={cn(
        'rounded-lg border-2 px-2 py-4',
        {
          'border-success':
            product.alternativeProductType ===
            AlternativeProductType.OutOfStockAlternative,
          'border-info':
            product.alternativeProductType ===
            AlternativeProductType.Classmates,
        },
        className,
      )}
    >
      <legend className="px-2 text-xl">{getTitle()}</legend>
      <div>
        <div className="relative">
          {showRemoveWarning && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/95 px-4 py-3">
              {/* <div className="max-w-m flex flex-col items-center gap-4 text-center sm:flex-row sm:text-left">
                <p className="text-sm">
                  <span className="font-semibold">
                    &ldquo;{product.productName}&rdquo;
                  </span>{' '}
                  will be removed from your basket
                </p>
                <Button variant="outline" size="sm">
                  Undo
                </Button>
              </div> */}
            </div>
          )}

          <div className="flex gap-2">
            <div className="relative size-[4.5rem] shrink-0 overflow-hidden rounded-lg bg-white lg:size-[5rem]">
              <FecomImage
                src={product.imageId ?? ''}
                className="object-contain"
                fecomMediaBaseUrl={fecomMediaBaseUrl}
                alt={product.productName ?? ''}
                height={100}
                width={100}
              />
            </div>
            {renderMobile()}
            {renderDesktop()}
          </div>
        </div>
      </div>
    </fieldset>
  );
}
